<template>
  <v-card>
    <v-card-title class="bg-blue-grey-lighten-5 text-primary">
      {{ t('chat.absenceModal.title') }}
    </v-card-title>

    <v-form ref="post_absence_form">
      <v-card-text class="py-3">
        <v-menu
          v-model="isDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          :disable-resize="true"
          min-width="auto">
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="dateRangeText"
              :label="t('chat.absenceModal.fields.period')"
              prepend-icon="mdi-calendar"
              :rules="[rules.required]"
              variant="outlined"
              density="compact"
              readonly
              v-bind="props"></v-text-field>
          </template>
          <v-date-picker
            v-model="dates"
            hide-header
            :locale="datePickerLocale"
            :key="`absence-picker-${datePickerLocale}`"
            :min="allowMinDate"
            multiple="range"
            view-mode="month" 
            @update:model-value="handleDateUpdate"
          >
            <template v-slot:actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" color="primary" @click="isDatePicker = false">
                {{ t('chat.absenceModal.actions.ok') }}
              </v-btn>
            </template>
          </v-date-picker>
        </v-menu>
        <div class="mt-4"></div>

        <v-select
          v-model="selectedReason"
          :items="reasonOptions"
          item-title="title"
          item-value="value"
          :rules="[rules.required]"
          :label="t('chat.absenceModal.fields.reason')"
          variant="outlined"
          density="compact"></v-select>

        <v-select
          v-if="selectedReason === 'infection'"
          v-model="infection"
          :items="infectionOptions"
          item-title="title"
          item-value="value"
          :rules="[rules.required]"
          :label="t('chat.absenceModal.fields.detail')"
          variant="outlined"
          class="infection-select"
          :menu-props="{ 
            contentClass: 'infection-select-menu',
          }"
          density="compact"></v-select>

        <v-textarea
          v-if="selectedReason === 'others'"
          v-model="comment"
          :rules="[rules.required, rules.counter500]"
          :label="t('chat.absenceModal.fields.comment')"
          variant="outlined"
          density="compact" />
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <div style="width: 20px;"></div>
        <v-btn @click="close" color="error" variant="text"> {{ t('common.cancel') }} </v-btn>
        <div style="width: 20px;"></div>
        <v-btn 
          @click="clickPost" 
          :loading="btnLoading" 
          color="primary" 
          variant="flat"
        >
          {{ t('common.submit') }}
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { t as translate, calendarLocale, getAppLocale } from '@/i18n';
import rules from '@/service/ValidRules';
import { chatModule } from '@/store/entityModules/chat';
import { meModule } from '@/store/entityModules/me';
import { formatDisplayDate } from '@/utils/dateFormat';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import 'dayjs/locale/km';

interface DataType {
  dates: any; // Vuetify 3 の range は [Date, Date] または単一Date
  startedAt: string;
  endedAt: string;
  reason: string;
  comment: string | null;
  selectedReason: string;
  infection: string;
  isDatePicker: boolean;
  btnLoading: boolean;
}

export default defineComponent({
  name: 'AbsenceModal',
  props: {
    isOpen: { type: Boolean, default: false },
    childId: { type: Number, default: NaN },
  },
  emits: ['close', 'posted'],
  
  data: (): DataType => ({
    dates: [],
    startedAt: '',
    endedAt: '',
    reason: '',
    comment: '',
    selectedReason: '',
    infection: '',
    isDatePicker: false,
    btnLoading: false,
  }),

  computed: {
    datePickerLocale(): string {
      // Depend on me.language so locale refreshes after profile / language change
      void meModule.me?.language;
      const loc = getAppLocale();
      dayjs.locale(loc);
      return calendarLocale(loc);
    },
    rules() { return rules; },
    form(): any { return this.$refs.post_absence_form; },
    allowMinDate(): any {
      return new Date(); // Vuetify 3 は Dateオブジェクトを期待
    },
    dateRangeText(): string {
      if (!this.dates || (Array.isArray(this.dates) && this.dates.length === 0)) {
        return '';
      }

      const dateArray = Array.isArray(this.dates) ? this.dates : [this.dates];
      // 昇順にソートして表示
      const sorted = [...dateArray]
        .filter(d => d) // null や undefined を除外
        .sort((a, b) => dayjs(a).unix() - dayjs(b).unix());

      if (sorted.length === 1) {
        return formatDisplayDate(sorted[0]);
      }
      return `${formatDisplayDate(sorted[0])} ~ ${formatDisplayDate(sorted[sorted.length - 1])}`;
    },
    reasonOptions() {
      return [
        { title: this.t('chat.absence.reasons.fever'), value: 'fever' },
        { title: this.t('chat.absence.reasons.personal'), value: 'personal' },
        { title: this.t('chat.absence.reasons.infection'), value: 'infection' },
        { title: this.t('chat.absence.reasons.othersWithComment'), value: 'others' },
      ];
    },
    infectionOptions() {
      return [
        { title: this.t('chat.absence.reasons.infStrep'), value: 'infStrep' },
        { title: this.t('chat.absence.reasons.infMycoplasma'), value: 'infMycoplasma' },
        { title: this.t('chat.absence.reasons.infHFMD'), value: 'infHFMD' },
        { title: this.t('chat.absence.reasons.infSlappedCheek'), value: 'infSlappedCheek' },
        { title: this.t('chat.absence.reasons.infStomach'), value: 'infStomach' },
        { title: this.t('chat.absence.reasons.infHerpangina'), value: 'infHerpangina' },
        { title: this.t('chat.absence.reasons.infRS'), value: 'infRS' },
        { title: this.t('chat.absence.reasons.infZoster'), value: 'infZoster' },
        { title: this.t('chat.absence.reasons.infRoseola'), value: 'infRoseola' },
        { title: this.t('chat.absence.reasons.infImpetigo'), value: 'infImpetigo' },
        { title: this.t('chat.absence.reasons.covid19'), value: 'covid19' },
      ];
    },
  },

  watch: {
    isOpen: {
      handler(val: boolean) {
        if (val) {
          this.dates = [];
          this.selectedReason = '';
          this.infection = '';
          this.comment = '';
        }
      },
      immediate: true,
    },
  },

  methods: {
    t(path: string, fallback = path, params: Record<string, string | number> = {}) {
      return translate(path, fallback, params);
    },
    handleDateUpdate(val: any) {
      // 2つ選択されたら自動的に閉じる、などの挙動が必要な場合はここに記述
    },
    close() {
      this.$emit('close');
    },
    async clickPost() {
      const { valid } = await this.form.validate();
      if (!valid) return;

      const dateArray = Array.isArray(this.dates) ? this.dates : [this.dates];
      if (dateArray.length === 0) return;

      const sorted = [...dateArray].sort((a, b) => dayjs(a).unix() - dayjs(b).unix());
      this.startedAt = dayjs(sorted[0]).format('YYYY-MM-DD');
      this.endedAt = dayjs(sorted[sorted.length - 1]).format('YYYY-MM-DD');

      if (this.selectedReason === 'infection') {
        this.reason = this.infection;
        this.comment = null;
      } else if (this.selectedReason === 'others') {
        this.reason = 'others';
      } else {
        this.reason = this.selectedReason;
        this.comment = null;
      }

      this.btnLoading = true;
        try {
          await chatModule.postHomeAbsence({
            childId: this.childId,
            startedAt: this.startedAt,
            endedAt: this.endedAt,
            reason: this.reason,
            comment: this.comment,
          });
          this.$emit('posted');
          this.$emit('close');
          this.btnLoading = false;
        } catch (error) {
          this.$emit('close');
          this.btnLoading = false;
        }
    },
  },
});
</script>
<style>
/* メニュー全体の幅と最小高さを制御 */
.infection-select-menu .v-list-item {
  min-width: 290px !important;
  min-height: 56px;
  padding-top: 20px;
  padding-bottom: 20px;
}

/* 選択肢リスト内の文字サイズと高さを制御 */
.infection-select-menu .v-list-item-title {
  font-size: 13px !important; /* ブラウザ制限により10px以下にならない場合があります */
}

.infection-select-menu .v-list-item {
  padding-inline: 8px !important; /* 左右の余白を詰める */
}
</style>

<style scoped>
/* 選択後の入力ボックス内の文字サイズ */
.infection-select :deep(.v-field__input) {
  font-size: 11px !important;
}
</style>